"use client";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  ComponentType,
} from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { usePathname } from "next/navigation";
import * as Sentry from "@sentry/nextjs";
import Button from "antd/es/button";
import Title from "antd/es/typography/Title";
import Paragraph from "antd/es/typography/Paragraph";

import LookupPhoneNumber, {
  CUSTOMER_BLOCKED_MESSAGE,
} from "./LookupPhoneNumber";
import RescheduleMain from "./RescheduleMain";
import FirstClient from "./FirstClient";
import VerifyRepeatClient from "./VerifyRepeatClient";
import RescheduleList from "./RescheduleList";
import SetupPayment from "./SetupPayment";
import AppointmentConfirmation from "./AppointmentConfirmation";

import {
  FIRST_TIME_CLIENT,
  VERIFY_REPEAT_CLIENT,
  LOOKUP_PHONE_NUMBER,
  RESCHEDULE_LIST,
  RESCHEDULE_MAIN,
  SETUP_PAYMENT,
  CONFIRMATION,
  MODAL_COMPONENT_SIZE,
  STRIPE_ELEMENTS_OPTIONS,
} from "./consts";

import { bookingFlowApi } from "@/lib/api/bookingFlowApi";
import { buildBookingSourceMetadata } from "@openings/booking-core";
import { captureBookingUtm } from "@/lib/bookingSource";
import {
  trackBookingConversion,
  trackBookingConversionAndWait,
  type BookingConversionInput,
} from "@/lib/googleTracking";
import {
  NewAppointmentState,
  CreateAppointmentArgs,
  RescheduleAppointmentArgs,
  NewAppointmentRoute,
  CompletionMode,
} from "./types";
import type { AppointmentForm } from "@/lib/hooks/useAppointment";
import { PoweredByOpenings } from "@openings/branding";
import InlineError from "./components/InlineError";
import { useLocale } from "@/lib/i18n";
import {
  dismissForPendingPayment,
  idempotencyErrorMessage,
  isPendingPaymentErrorCode,
  pendingPaymentNoticeFor,
  type PendingPaymentNotice,
} from "./paymentPendingTransition";
import styles from "./appointmentFlow.module.css";
import { getStripeHarnessOverride } from "./stripeHarnessSeam";

// ---- Stripe Setup (module-singleton) ----
const stripePublishableKey =
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY?.trim();
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;

// ---- Helpers ----
const makeInitialState = (
  creditCardRequired: boolean,
  _verificationMethod?: "SMS" | "EMAIL",
): NewAppointmentState => ({
  route: LOOKUP_PHONE_NUMBER,
  state: {
    upcomingAppointments: [],
    phoneNumber: "",
    customerId: undefined,
    creditCardRequired,
    paymentMethodId: undefined,
  },
});

const isCreditCardRequired = (
  services?: Array<{
    hasPayAdvance?: boolean;
    paymentRequirement?: "NONE" | "DEPOSIT" | "FULL";
  }>,
) =>
  Boolean(
    services?.some(
      (s) =>
        s?.hasPayAdvance ||
        s?.paymentRequirement === "DEPOSIT" ||
        s?.paymentRequirement === "FULL",
    ),
  );

const buildServiceRefs = (
  services: Array<{ id: string; option?: { id?: string } }>,
) =>
  services.map((s) => ({
    id: s.id,
    ...(s.option?.id ? { optionId: s.option.id } : {}),
  }));

const createBookingIdempotencyKey = (): string => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `booking-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`;
};

type ValidatedBookingConversion = NonNullable<
  Awaited<
    ReturnType<typeof bookingFlowApi.resumeAdsAutopilotAttribution>
  >["data"]["conversion"]
>;

function toBookingConversionInput(
  conversion: ValidatedBookingConversion,
): BookingConversionInput {
  return {
    conversionEventId: conversion.eventId,
    value: conversion.value,
    currency: conversion.currency,
    ...(conversion.managedGoogleAds
      ? { managedGoogleAds: conversion.managedGoogleAds }
      : {}),
  };
}

// ---- Route → Component map ----
const componentMap: Record<
  Exclude<NewAppointmentRoute, typeof CONFIRMATION>,
  ComponentType<any>
> = {
  [LOOKUP_PHONE_NUMBER]: LookupPhoneNumber,
  [SETUP_PAYMENT]: SetupPayment,
  [FIRST_TIME_CLIENT]: FirstClient,
  [VERIFY_REPEAT_CLIENT]: VerifyRepeatClient,
  [RESCHEDULE_MAIN]: RescheduleMain,
  [RESCHEDULE_LIST]: RescheduleList,
};

export interface AppointmentFlowContentProps {
  appointmentForm: AppointmentForm;
  onClose: () => void;
  /**
   * Controls post-booking behavior.
   * - "redirect" (default): navigate to /appointment/review page via hard navigation
   * - "inline": show confirmation inside the modal/drawer
   */
  completionMode?: CompletionMode;
  /**
   * Whether to render the "Powered by Openings" footer inline.
   * Set to false when the container (e.g. mobile drawer) renders its own
   * sticky footer externally. Defaults to true.
   */
  showFooter?: boolean;
}

const AppointmentFlowContent: React.FC<AppointmentFlowContentProps> = ({
  appointmentForm,
  onClose,
  completionMode = "redirect",
  showFooter = true,
}) => {
  const pathname = usePathname();
  const { t } = useLocale();
  // Read once: <Elements> does not accept a changed `stripe` prop.
  const [stripe] = useState(() => getStripeHarnessOverride() ?? stripePromise);
  const [flowError, setFlowError] = useState<string | null>(null);
  // Set once a create ends with an unknown payment outcome. Terminal: the
  // card step never comes back for this mounted form.
  const [pendingPayment, setPendingPayment] =
    useState<PendingPaymentNotice | null>(null);
  // One key per mounted appointment form. It deliberately survives the
  // 428 → verification flow, card-step retries, and manual retries after a
  // lost/ambiguous response. Closing the form unmounts this component, so the
  // next logical booking gets a fresh key.
  const createIdempotencyKeyRef = useRef<string | null>(null);

  const [state, setState] = useState<NewAppointmentState>(
    makeInitialState(
      isCreditCardRequired(appointmentForm?.services),
      appointmentForm?.verificationMethod,
    ),
  );

  const creditCardRequired = isCreditCardRequired(appointmentForm?.services);

  // Clear flow-level error when the step changes
  useEffect(() => {
    setFlowError(null);
  }, [state.route]);

  // Close form if navigating to the review page
  useEffect(() => {
    if (pathname.includes("/appointment/review")) {
      Sentry.addBreadcrumb({
        category: "booking",
        level: "info",
        message: "modal:close-on-review-pathname",
        data: { pathname },
      });
      onClose();
    }
  }, [pathname, onClose]);

  /**
   * Handle successful booking completion.
   * - "inline": route to the in-modal confirmation screen
   * - "redirect": navigate to the full review page (or call onAfter)
   */
  const handleBookingSuccess = useCallback(
    (appointmentId: string, customerId?: string) => {
      Sentry.addBreadcrumb({
        category: "booking",
        level: "info",
        message: "handleBookingSuccess",
        data: {
          appointmentId,
          completionMode,
          hasOnAfter: Boolean(appointmentForm.onAfter),
          salonId: appointmentForm.salonId,
        },
      });
      appointmentForm.onBookingComplete?.();
      if (appointmentForm.onAfter) {
        appointmentForm.onAfter(appointmentId);
        onClose();
        return;
      }
      if (completionMode === "inline") {
        setState((prev) => ({
          route: CONFIRMATION,
          state: {
            ...prev.state,
            appointmentId,
            customerId: customerId ?? prev.state.customerId,
          },
        }));
      } else {
        // Cross into the token-bearing review route with a new document. The
        // destination suppresses Google tracking before scripts initialize;
        // an SPA history update could expose the appointment ID to remotely
        // configured Enhanced Measurement before React observes the route.
        Sentry.captureMessage("booking:redirect-to-review", {
          level: "info",
          tags: {
            booking_step: "redirect",
            salonId: appointmentForm.salonId,
          },
          extra: {
            appointmentId,
            completionMode,
            target: `/appointment/review/${appointmentId}`,
          },
        });
        window.location.assign(`/appointment/review/${appointmentId}`);
      }
    },
    [appointmentForm, completionMode, onClose],
  );

  // Create appointment
  const onCreateAppointment = useCallback(
    async (args: CreateAppointmentArgs) => {
      Sentry.addBreadcrumb({
        category: "booking",
        level: "info",
        message: "onCreateAppointment:start",
        data: {
          salonId: appointmentForm.salonId,
          userId: appointmentForm.userId,
          scheduleId: appointmentForm.scheduleId,
          customerId: args.customerId,
          hasPaymentMethod: Boolean(args.paymentMethodId),
          hasVerificationCode: Boolean(args.verificationCode),
          completionMode,
        },
      });
      try {
        const basePayload = {
          salonId: appointmentForm.salonId,
          userId: appointmentForm.userId,
          scheduleId: appointmentForm.scheduleId,
          customerId: args.customerId,
          paymentMethodId: args.paymentMethodId,
          services: buildServiceRefs(appointmentForm.services),
          date: appointmentForm.date,
          time: appointmentForm.time,
          verificationCode: args.verificationCode,
          metadata: buildBookingSourceMetadata({
            // Within apps/web, an inline completion is the standalone subdomain
            // booking page; a redirect completion is the website template.
            surface:
              completionMode === "inline"
                ? "subdomain-booking-page"
                : "website-template",
            sourceApp: "openings-web",
            utm: captureBookingUtm(),
          }),
        };

        const hasRequestId = Boolean(appointmentForm.requestId);
        const hasQuotePublicId = Boolean(appointmentForm.quotePublicId);
        if (hasRequestId !== hasQuotePublicId) {
          throw new Error(
            "This quote link is incomplete. Request a new quote.",
          );
        }

        const payload: Parameters<typeof bookingFlowApi.createAppointment>[0] =
          appointmentForm.requestId && appointmentForm.quotePublicId
            ? {
                ...basePayload,
                requestId: appointmentForm.requestId,
                quotePublicId: appointmentForm.quotePublicId,
              }
            : basePayload;

        const idempotencyKey =
          createIdempotencyKeyRef.current ?? createBookingIdempotencyKey();
        createIdempotencyKeyRef.current = idempotencyKey;
        const res = await bookingFlowApi.createAppointment(payload, {
          idempotencyKey,
        });
        const appointmentId = res.data.appointmentId;
        const conversion = res.data.conversion;
        if (conversion) {
          try {
            const trackingInput = toBookingConversionInput(conversion);
            const conversionDispatched =
              completionMode === "redirect"
                ? await trackBookingConversionAndWait(trackingInput)
                : trackBookingConversion(trackingInput);
            Sentry.addBreadcrumb({
              category: "booking",
              level: "info",
              message: "onCreateAppointment:conversion",
              data: {
                dispatched: conversionDispatched,
                hasAuthoritativeValue: conversion.value !== null,
                salonId: appointmentForm.salonId,
              },
            });
          } catch (trackingError) {
            // Analytics must never interrupt a successfully committed booking.
            Sentry.captureException(trackingError, {
              tags: {
                booking_step: "conversion",
                salonId: appointmentForm.salonId,
              },
            });
          }
        }
        Sentry.addBreadcrumb({
          category: "booking",
          level: "info",
          message: "onCreateAppointment:success",
          data: { appointmentId, salonId: appointmentForm.salonId },
        });
        handleBookingSuccess(appointmentId, args.customerId);
      } catch (e) {
        args.errorCallback?.();
        const error = e as Error & { code?: string };
        const errorMessage = error.message || "An error occurred.";
        if (isPendingPaymentErrorCode(error.code)) {
          // One immediate, idempotent check catches the webhook-won race. A
          // server-only resume cookie gates later-document checks; a null or
          // failed result stays eligible without exposing the signed session.
          void bookingFlowApi
            .resumeAdsAutopilotAttribution()
            .then((result) => {
              if (result.data.conversion) {
                trackBookingConversion(
                  toBookingConversionInput(result.data.conversion),
                );
              }
            })
            .catch(() => undefined);
        }
        Sentry.captureMessage("booking:create-failed", {
          level: "warning",
          tags: {
            booking_step: "create",
            error_code: error.code ?? "unknown",
            salonId: appointmentForm.salonId,
          },
          extra: {
            errorMessage,
            customerId: args.customerId,
            scheduleId: appointmentForm.scheduleId,
            userId: appointmentForm.userId,
            completionMode,
          },
        });
        if (error.code === "VERIFICATION_REQUIRED") {
          setState((prev) => ({
            route: VERIFY_REPEAT_CLIENT,
            state: {
              ...prev.state,
              verificationCode: undefined,
              customerId: args.customerId,
              paymentMethodId:
                args.paymentMethodId ?? prev.state.paymentMethodId,
              verificationRequired: true,
              verificationReason: errorMessage,
              verificationPurpose: `booking:${appointmentForm.salonId}`,
            },
          }));
          setFlowError("Please verify your identity to continue.");
          return;
        }
        if (error.code === "VERIFICATION_FAILED") {
          setState((prev) => ({
            route: VERIFY_REPEAT_CLIENT,
            state: {
              ...prev.state,
              verificationCode: undefined,
              customerId: args.customerId,
              paymentMethodId:
                args.paymentMethodId ?? prev.state.paymentMethodId,
              verificationRequired: true,
              verificationReason: errorMessage,
              verificationPurpose: `booking:${appointmentForm.salonId}`,
            },
          }));
          setFlowError(
            errorMessage || "Verification failed. Please try again.",
          );
          return;
        }
        if (error.code === "BOOKING_BLOCKED") {
          setFlowError(errorMessage);
          return;
        }
        if (
          dismissForPendingPayment(
            error.code,
            appointmentForm.onPaymentPending,
            onClose,
          )
        ) {
          return;
        }
        const pendingNotice = pendingPaymentNoticeFor(error.code);
        if (pendingNotice) {
          setPendingPayment(pendingNotice);
          return;
        }
        const retryMessage = idempotencyErrorMessage(error.code, t);
        if (retryMessage) {
          setFlowError(retryMessage);
          return;
        }
        if (errorMessage === "Customer not found") {
          setState((prev) => ({
            route: FIRST_TIME_CLIENT,
            state: {
              ...prev.state,
              customerId: undefined,
            },
          }));
          setFlowError(
            "We couldn't match an existing customer record. Please confirm your details to continue.",
          );
          return;
        }
        setFlowError(errorMessage);
      }
    },
    [appointmentForm, completionMode, handleBookingSuccess, onClose, t],
  );

  // Reschedule appointment
  const onRescheduleAppointment = useCallback(
    async (args: RescheduleAppointmentArgs) => {
      Sentry.addBreadcrumb({
        category: "booking",
        level: "info",
        message: "onRescheduleAppointment:start",
        data: {
          salonId: appointmentForm.salonId,
          scheduleId: appointmentForm.scheduleId,
          previousAppointmentId: args.previousAppointmentId,
          hasPaymentMethod: Boolean(args.paymentMethodId),
          hasVerificationCode: Boolean(args.verificationCode),
        },
      });
      try {
        const payload = {
          userId: appointmentForm.userId,
          rescheduleAppointmentId: args.previousAppointmentId,
          scheduleId: appointmentForm.scheduleId,
          paymentMethodId: args.paymentMethodId,
          services: buildServiceRefs(appointmentForm.services),
          date: appointmentForm.date,
          time: appointmentForm.time,
          verificationCode: args.verificationCode,
          phoneNumber: state.state.phoneNumber || undefined,
          email: state.state.email || undefined,
          metadata: buildBookingSourceMetadata({
            surface:
              completionMode === "inline"
                ? "subdomain-booking-page"
                : "website-template",
            sourceApp: "openings-web",
            utm: captureBookingUtm(),
          }),
        };

        const res = await bookingFlowApi.rescheduleAppointment(payload);
        const appointmentId = res.data.appointmentId;
        Sentry.addBreadcrumb({
          category: "booking",
          level: "info",
          message: "onRescheduleAppointment:success",
          data: { appointmentId, salonId: appointmentForm.salonId },
        });
        handleBookingSuccess(appointmentId);
      } catch (e) {
        args.errorCallback?.();
        const error = e as Error & { code?: string };
        Sentry.captureMessage("booking:reschedule-failed", {
          level: "warning",
          tags: {
            booking_step: "reschedule",
            error_code: error.code ?? "unknown",
            salonId: appointmentForm.salonId,
          },
          extra: {
            errorMessage: error.message,
            previousAppointmentId: args.previousAppointmentId,
            scheduleId: appointmentForm.scheduleId,
          },
        });
        // Refusals the customer can't fix by retrying map by code to the
        // booking standard's copy; the original appointment is unchanged.
        if (error.code === "CUSTOMER_BLOCKED") {
          setFlowError(CUSTOMER_BLOCKED_MESSAGE);
          return;
        }
        if (error.code === "RESCHEDULE_PREPAYMENT_REQUIRED") {
          setFlowError(t.reschedulePrepaymentRequired);
          return;
        }
        setFlowError(error.message || "An error occurred.");
      }
    },
    [
      appointmentForm,
      completionMode,
      handleBookingSuccess,
      state.state.phoneNumber,
      state.state.email,
      t,
    ],
  );

  // Unknown payment outcome (quote pages close into their own pending state
  // instead). No card form and no Pay button: closing is the only exit.
  if (pendingPayment) {
    return (
      <div
        className={`${styles.flowShell} openings-appointment-flow`}
        data-booking-flow-step="payment-pending"
      >
        <main className={styles.confirmation}>
          <Title level={3}>{t.paymentPendingTitle}</Title>
          <div role="alert">
            <Paragraph>
              {pendingPayment === "charged"
                ? t.paymentPendingChargedBody
                : t.paymentPendingBody}
            </Paragraph>
          </div>
          <Button
            type="primary"
            size={MODAL_COMPONENT_SIZE}
            block
            onClick={onClose}
          >
            {t.close}
          </Button>
        </main>
        {showFooter && (
          <div className={styles.flowFooter}>
            <PoweredByOpenings />
          </div>
        )}
      </div>
    );
  }

  // Confirmation screen (inline completion)
  if (state.route === CONFIRMATION) {
    return (
      <div
        className={`${styles.flowShell} openings-appointment-flow`}
        data-booking-flow-step="confirmation"
      >
        <AppointmentConfirmation
          appointmentForm={appointmentForm}
          appointmentId={state.state.appointmentId}
          customerId={state.state.customerId}
          onDone={onClose}
        />
        {showFooter && (
          <div className={styles.flowFooter}>
            <PoweredByOpenings />
          </div>
        )}
      </div>
    );
  }

  const CurrentComponent = componentMap[state.route];

  return (
    <Elements stripe={stripe} options={STRIPE_ELEMENTS_OPTIONS}>
      <div
        className={`${styles.flowShell} openings-appointment-flow`}
        data-booking-flow-step={state.route}
      >
        <div className={styles.stepContainer}>
          <InlineError message={flowError} />
          <CurrentComponent
            appointmentDetails={appointmentForm}
            state={{
              ...state,
              state: { ...state.state, creditCardRequired },
            }}
            setState={setState}
            onCreateAppointment={onCreateAppointment}
            onRescheduleAppointment={onRescheduleAppointment}
          />
        </div>
        {showFooter && (
          <div className={styles.flowFooter}>
            <PoweredByOpenings />
          </div>
        )}
      </div>
    </Elements>
  );
};

export default AppointmentFlowContent;
